import React from "react";
import {
  DataTable,
  DataTableStateEvent,
  DataTableSortEvent,
} from "primereact/datatable";
import { Column } from "primereact/column";
import { HighValueAtRiskData } from "../../../models/customer-analytics/customer-analytics.model";
import CallStatusCell from "../shared/call-status-cell";

interface ChurnDataTableProps {
  data: HighValueAtRiskData[];
  pageNumber: number;
  pageSize: number;
  totalRecords: number;
  sortField: string;
  sortOrder: 1 | -1;
  onPageChange: (event: DataTableStateEvent) => void;
  onSortChange: (field: string, order: string) => void;
  refreshData: () => void;
}

const getUrgencyClass = (days: number): string => {
  if (days > 90) return "ca-urgency-critical";
  if (days > 60) return "ca-urgency-high";
  if (days > 30) return "ca-urgency-medium";
  return "ca-urgency-low";
};

const ChurnDataTable: React.FC<ChurnDataTableProps> = (props) => {
  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: "INR",
      maximumFractionDigits: 0,
    }).format(value);
  };

  const formatDate = (dateString: string) => {
    if (!dateString) return "-";
    const date = new Date(dateString);
    const day = date.getDate().toString().padStart(2, "0");
    const month = date.toLocaleString("en-GB", { month: "short" });
    const year = date.getFullYear();
    return `${day} ${month} ${year}`;
  };

  const onSort = (e: DataTableSortEvent) => {
    const order = e.sortOrder === 1 ? "ASC" : "DESC";
    props.onSortChange(e.sortField, order);
  };

  const daysBody = (rowData: HighValueAtRiskData) => (
    <span className={`ca-badge ${getUrgencyClass(rowData.daysSinceLastOrder)}`}>
      {rowData.daysSinceLastOrder} days
    </span>
  );

  const callDataBody = (rowData: HighValueAtRiskData) => (
    <CallStatusCell callDataJson={rowData.callData} />
  );

  return (
    <div className="col-12">
      <div className="card border-noround p-2">
        <DataTable
          value={props.data}
          paginator
          rows={props.pageSize}
          rowsPerPageOptions={[10, 25, 50, 100]}
          totalRecords={props.totalRecords}
          lazy
          paginatorTemplate="RowsPerPageDropdown FirstPageLink PrevPageLink CurrentPageReport NextPageLink LastPageLink"
          currentPageReportTemplate="{first} to {last} of {totalRecords}"
          first={(props.pageNumber - 1) * props.pageSize}
          onPage={props.onPageChange}
          sortMode="single"
          sortField={props.sortField}
          sortOrder={props.sortOrder}
          onSort={onSort}
          scrollable
          scrollHeight="flex"
          responsiveLayout="scroll"
          emptyMessage="No records found"
          className="ca-datatable"
        >
          <Column field="name" header="Customer" />
          <Column field="primaryContactNbr" header="Phone Number" />
          <Column field="totalOrders" header="Orders" sortable />
          <Column
            field="lifetimeRevenue"
            header="Lifetime Revenue"
            body={(row) => formatCurrency(row.lifetimeRevenue)}
            sortable
          />
          <Column
            field="avgOrderValue"
            header="Avg Order"
            body={(row) => formatCurrency(row.avgOrderValue)}
            sortable
          />
          <Column
            field="lastOrderDate"
            header="Last Order"
            body={(row) => formatDate(row.lastOrderDate)}
          />
          <Column
            field="daysSinceLastOrder"
            header="Days Inactive"
            body={daysBody}
            sortable
          />
          <Column header="Last Call Status" body={callDataBody} style={{ minWidth: "140px" }} />
        </DataTable>
      </div>
    </div>
  );
};

export default ChurnDataTable;
